import {
    Avatar,
    Badge,
    Box,
    Button,
    ButtonGroup,
    Flex,
    HStack,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Td,
    Text,
    Tooltip,
    Tr,
    useClipboard,
    useColorModeValue,
} from "@chakra-ui/react";
import React from "react";
import {FILE_BASE_URL} from "../../../constants/ApiConstant";
import {ChevronDownIcon, CheckIcon, CopyIcon, EmailIcon, PhoneIcon} from "@chakra-ui/icons";
import {FiMapPin} from "react-icons/fi";
import VerifiedTick from "../../../components/VerifiedTick/VerifiedTick";

// TEMP placeholder so both badge styles are visible before the backend sends a
// `verificationTier`. Remove this once the real field is wired in.
const demoVerificationTier = (school) =>
    (school?.name || "").trim().length % 2 === 0 ? "gold" : "silver";

// Small pill that shows a value with an icon and, when copyable, a copy button
// that flips to a check for a moment after copying.
function CopyableField({icon, value, label, canCopy = false}) {
    const {hasCopied, onCopy} = useClipboard(value || "");
    const iconColor = useColorModeValue("gray.400", "gray.500");
    const valueColor = useColorModeValue("gray.600", "gray.300");

    if (!value) return null;

    return (
        <HStack spacing="6px" align="center">
            <Icon as={icon} color={iconColor} boxSize="12px"/>
            <Text fontSize="sm" color={valueColor} fontWeight="normal" noOfLines={1}>
                {value}
            </Text>
            {canCopy && (
                <Tooltip label={hasCopied ? "Copied!" : `Copy ${label || "value"}`} placement="top" hasArrow>
                    <IconButton
                        aria-label={`Copy ${label || "value"}`}
                        icon={hasCopied ? <CheckIcon/> : <CopyIcon/>}
                        size="xs"
                        variant="ghost"
                        colorScheme={hasCopied ? "green" : "gray"}
                        minW="auto"
                        h="18px"
                        onClick={(e) => {
                            e.stopPropagation();
                            onCopy();
                        }}
                    />
                </Tooltip>
            )}
        </HStack>
    );
}

function SchoolTableRow(props) {

    const {serial, school, setShowSchoolDetail, onEditSchool} = props;
    const textColor = useColorModeValue("gray.700", "white");
    const bgStatus = useColorModeValue("gray.400", "#1a202c");
    const colorStatus = useColorModeValue("white", "gray.400");
    const cardBg = useColorModeValue("gray.50", "whiteAlpha.100");
    const cardBorder = useColorModeValue("gray.100", "whiteAlpha.200");
    const statusBg = school?.status === "Y" ? "green.400" : school?.status === "B" ? "red.400" : bgStatus;
    const statusLabel = school?.status === "Y" ? "Active" : school?.status === "B" ? "Blocked" : school?.status === "N" ? "In active" : "Inactive";

    const logoSrc = school?.schoolLogo
        ? (school.schoolLogo.startsWith("http") ? school.schoolLogo : `${FILE_BASE_URL}${school.schoolLogo}`)
        : undefined;

    const showSchoolDetail = (id) => {
        setShowSchoolDetail(id, true);
    }

    return (
        <Tr>
            {/* Serial number — continuous across pages. */}
            <Td px="4px" pl="0px" textAlign="center" verticalAlign="middle">
                <Text fontSize="sm" color={textColor} fontWeight="bold">
                    {serial}
                </Text>
            </Td>

            {/* School identity card — logo, name, address, email, copyable phone. */}
            <Td minWidth={{base: "200px", xl: "240px"}} px="4px">
                <Box
                    bg={cardBg}
                    border="1px solid"
                    borderColor={cardBorder}
                    borderRadius="12px"
                    p="8px 10px"
                    minWidth="100%"
                >
                    <Flex align="center" flexWrap="nowrap" gap="10px">
                        <Avatar src={logoSrc} name={school?.name} w="40px" h="40px" borderRadius="10px"/>
                        <Flex direction="column" gap="2px" minW="0">
                            <HStack spacing="6px" align="center" minW="0">
                                <Text fontSize="sm" color={textColor} fontWeight="bold" noOfLines={1}>
                                    {school?.name}
                                </Text>
                                {/* Verification badge. TODO: swap to the backend field once
                                    available — e.g. tier={school?.verificationTier}. */}
                                <VerifiedTick tier={school?.verificationTier || demoVerificationTier(school)} size="sm"/>
                            </HStack>
                            {/* Type / subType / channel as interactive badges. */}
                            {(school?.type || school?.subType || school?.channelCode) && (
                                <HStack spacing="6px" mt="1px" mb="2px" flexWrap="wrap">
                                    {school?.channelCode && (
                                        <Tooltip label="Channel" placement="top" hasArrow>
                                            <Badge
                                                bg="green.400"
                                                color="white"
                                                fontSize="11px"
                                                px="8px"
                                                py="2px"
                                                borderRadius="full"
                                                textTransform="none"
                                                cursor="default"
                                            >
                                                {school.channelCode}
                                            </Badge>
                                        </Tooltip>
                                    )}
                                    {school?.type && (
                                        <Tooltip label="School type" placement="top" hasArrow>
                                            <Badge
                                                colorScheme="purple"
                                                variant="subtle"
                                                fontSize="11px"
                                                px="8px"
                                                py="2px"
                                                borderRadius="full"
                                                textTransform="none"
                                                cursor="default"
                                            >
                                                {school.type}
                                            </Badge>
                                        </Tooltip>
                                    )}
                                    {school?.subType && (
                                        <Tooltip label="Sub type" placement="top" hasArrow>
                                            <Badge
                                                colorScheme="blue"
                                                variant="subtle"
                                                fontSize="11px"
                                                px="8px"
                                                py="2px"
                                                borderRadius="full"
                                                textTransform="none"
                                                cursor="default"
                                            >
                                                {school.subType}
                                            </Badge>
                                        </Tooltip>
                                    )}
                                </HStack>
                            )}
                            <CopyableField icon={FiMapPin} value={school?.address}/>
                        </Flex>
                    </Flex>
                </Box>
            </Td>

            {/* Contact info — email & phone pulled into their own column so the
                identity card stays compact and the layout is consistent. */}
            <Td px="4px">
                <Box
                    bg={cardBg}
                    border="1px solid"
                    borderColor={cardBorder}
                    borderRadius="12px"
                    p="8px 10px"
                    minWidth="150px"
                >
                    <Text fontSize="10px" color="gray.400" fontWeight="normal" mb="4px">
                        Contact Info
                    </Text>
                    {(school?.email || school?.phone) ? (
                        <Flex direction="column" gap="6px">
                            <CopyableField icon={EmailIcon} value={school?.email} label="email" canCopy/>
                            <CopyableField icon={PhoneIcon} value={school?.phone} label="phone number" canCopy/>
                        </Flex>
                    ) : (
                        <Text fontSize="sm" color="gray.400">—</Text>
                    )}
                </Box>
            </Td>

            <Td px="4px">
                <Flex direction="column">
                    <Text fontSize="sm" color={textColor} fontWeight="bold">
                        Roster: {school?.totalRoster || 0}
                    </Text>
                    <Text fontSize="xs" color="gray.400" fontWeight="normal">
                        Bell: {school?.totalBell || 0}
                    </Text>
                </Flex>
            </Td>

            <Td px="4px">
                <Badge
                    bg={statusBg}
                    color={colorStatus}
                    fontSize="12px"
                    p="2px 8px"
                    borderRadius="8px"
                >
                    {statusLabel}
                </Badge>
            </Td>

            <Td px="4px">
                <ButtonGroup isAttached variant="outline" size="sm">
                    <Button>Action</Button>
                    <Menu>
                        <MenuButton
                            as={IconButton}
                            icon={<ChevronDownIcon/>}
                            aria-label="Options"
                        />
                        <MenuList>
                            <MenuItem onClick={() => showSchoolDetail(school?.id)}>View</MenuItem>
                            <MenuItem onClick={() => onEditSchool && onEditSchool(school?.id)}>Edit</MenuItem>
                            <MenuItem onClick={() => showSchoolDetail(school?.id)}>Extend Plan</MenuItem>
                            <MenuItem onClick={() => alert("Option 2 selected")}>Delete</MenuItem>
                        </MenuList>
                    </Menu>
                </ButtonGroup>
            </Td>
        </Tr>
    );
}

export default SchoolTableRow;
